/**
 * SavedSearchDetail — view / edit a single saved search.
 *
 * - Schedule editor: a labelled cron-string field, persisted via PATCH.
 * - Run now: triggers POST /:id/run and prepends the result to the run history.
 * - Run history: an MUI X DataGrid. The C15 read API does not (yet) return a
 *   run log, so we surface the persisted ``lastRunAt`` plus any runs triggered
 *   in this session via "Run now". Each row reports hits / new hits.
 * - Delete: guarded by a confirmation dialog; on success navigates to /saved.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  useDeleteSavedSearch,
  useRunSavedSearch,
  useSavedSearch,
  useUpdateSavedSearch,
} from "./hooks/useSavedSearches";
import type { RunSavedSearchResponse } from "@records-monitor/shared";

export interface SavedSearchDetailProps {
  id: string;
}

interface RunRow {
  id: string;
  ranAt: string;
  hits: number;
  newHits: number;
  status: string;
}

function formatDate(value: string | null): string {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Never" : date.toLocaleString();
}

export function SavedSearchDetail({ id }: SavedSearchDetailProps): ReactElement {
  const { data: search, isLoading, isError } = useSavedSearch(id);
  const update = useUpdateSavedSearch(id);
  const run = useRunSavedSearch(id);
  const del = useDeleteSavedSearch();
  const navigate = useNavigate();

  const [schedule, setSchedule] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sessionRuns, setSessionRuns] = useState<readonly RunRow[]>([]);

  // Seed the schedule field from the loaded search (one-way: server → field).
  useEffect(() => {
    if (search) setSchedule(search.schedule ?? "");
  }, [search]);

  const handleSaveSchedule = (): void => {
    update.mutate({ schedule: schedule.trim() === "" ? null : schedule.trim() });
  };

  const handleRunNow = (): void => {
    run.mutate(undefined, {
      onSuccess: (result: RunSavedSearchResponse) => {
        setSessionRuns((prev) => [
          {
            id: result.runId,
            ranAt: new Date().toISOString(),
            hits: result.hits,
            newHits: result.newHits,
            status: result.status,
          },
          ...prev,
        ]);
      },
    });
  };

  const handleConfirmDelete = (): void => {
    del.mutate(id, {
      onSuccess: () => {
        setConfirmOpen(false);
        void navigate({ to: "/saved" });
      },
    });
  };

  const columns = useMemo<GridColDef<RunRow>[]>(
    () => [
      { field: "ranAt", headerName: "Ran at", flex: 1.4, minWidth: 180 },
      { field: "hits", headerName: "Hits", width: 90 },
      { field: "newHits", headerName: "New hits", width: 100 },
      { field: "status", headerName: "Status", width: 120 },
    ],
    [],
  );

  // Session runs (newest first) with their timestamps formatted for display,
  // followed by the persisted last-run row when it isn't already shown.
  const displayRows = useMemo<RunRow[]>(() => {
    const rows: RunRow[] = sessionRuns.map((r) => ({ ...r, ranAt: formatDate(r.ranAt) }));
    if (search?.lastRunAt && sessionRuns.length === 0) {
      rows.push({
        id: `last-${search.id}`,
        ranAt: formatDate(search.lastRunAt),
        hits: search.lastSeenSet.length,
        newHits: 0,
        status: "completed",
      });
    }
    return rows;
  }, [search, sessionRuns]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress aria-label="Loading saved search" />
      </Box>
    );
  }

  if (isError || !search) {
    return (
      <Alert severity="error" role="alert">
        Could not load this saved search.
      </Alert>
    );
  }

  const queryText = typeof search.query["q"] === "string" ? (search.query["q"] as string) : "";

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" component="h2">
          {queryText || "(no query)"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {search.type} · Last run: {formatDate(search.lastRunAt)}
        </Typography>
      </Box>

      <Box component="section" aria-label="Schedule">
        <Typography variant="subtitle1" gutterBottom>
          Schedule
        </Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <TextField
            label="Schedule (cron)"
            value={schedule}
            onChange={(event) => setSchedule(event.target.value)}
            placeholder="0 8 * * *"
            helperText="Leave empty to disable the schedule."
            size="small"
          />
          <Button variant="contained" onClick={handleSaveSchedule} disabled={update.isPending}>
            Save schedule
          </Button>
        </Stack>
        {update.isError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            Could not update the schedule.
          </Alert>
        ) : null}
      </Box>

      <Box component="section" aria-label="Run history">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            Run history
          </Typography>
          <Button variant="outlined" onClick={handleRunNow} disabled={run.isPending}>
            Run now
          </Button>
        </Stack>
        {run.isError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            Run failed. Please try again.
          </Alert>
        ) : null}
        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={displayRows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            aria-label="Run history table"
            localeText={{ noRowsLabel: "No runs yet. Use “Run now”." }}
          />
        </Box>
      </Box>

      <Box component="section" aria-label="Danger zone">
        <Button color="error" variant="outlined" onClick={() => setConfirmOpen(true)}>
          Delete saved search
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="delete-saved-title"
      >
        <DialogTitle id="delete-saved-title">Delete this saved search?</DialogTitle>
        <DialogContentText>
          <DialogContent>
            This permanently removes the saved search and stops its scheduled runs.
          </DialogContent>
        </DialogContentText>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={del.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
